
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TestResult } from "@/hooks/useTestResults";
import { calculateAverageScore } from "@/utils/testDataProcessing";
import { ProfileHeader } from "./child-card/ProfileHeader";
import { ProgressSection } from "./child-card/ProgressSection";
import { BadgesSection } from "./child-card/BadgesSection";
import { RecentAchievement } from "./child-card/RecentAchievement";

interface ChildCardProps {
  id: string;
  name: string;
  age: number;
  gradeLevel: string;
  badges?: string[];
  profilePictureUrl?: string;
  birthday?: string;
}

export const ChildCard = ({ 
  id, 
  name, 
  age,
  badges = [],
  profilePictureUrl,
  birthday
}: ChildCardProps) => {
  const navigate = useNavigate();

  const { data: testResults, isLoading } = useQuery({
    queryKey: ["testResults", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("test_results")
        .select(`
          id,
          score,
          test_id,
          created_at,
          tests (
            id,
            category
          )
        `)
        .eq("child_id", id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as TestResult[];
    }
  });

  // Filter test results to only keep the latest attempt for each test
  const latestTestResults = testResults?.reduce((acc: TestResult[], result) => {
    const existingIndex = acc.findIndex(r => r.test_id === result.test_id);
    if (existingIndex === -1) {
      acc.push(result);
    } else if (new Date(result.created_at) > new Date(acc[existingIndex].created_at)) {
      acc[existingIndex] = result;
    }
    return acc;
  }, []);

  const averageScore = latestTestResults ? calculateAverageScore(latestTestResults) : 0;

  return (
    <Card 
      className="hover:shadow-lg transition-shadow cursor-pointer bg-white border-2 hover:border-primary"
      onClick={() => navigate(`/child/${id}`)}
    >
      <CardContent className="p-4 sm:p-6">
        <div className="space-y-4">
          <ProfileHeader
            name={name}
            age={age}
            profilePictureUrl={profilePictureUrl}
            averageScore={averageScore}
            birthday={birthday}
          />
          
          <RecentAchievement 
            testResults={testResults}
            isLoading={isLoading}
          />

          <ProgressSection averageScore={averageScore} />
          
          <BadgesSection badges={badges} />
        </div>
      </CardContent>
    </Card>
  );
};
